
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SeoMetadata from "@/components/common/SeoMetadata";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BarChart3, Clock, CreditCard, Wallet, HelpCircle, ArrowLeft, CheckCircle, AlertCircle, FileText, Gauge } from "lucide-react";
import { motion } from "framer-motion";
import { LainaCta } from "@/components/common/LainaCta";
import RelatedArticles from "@/components/articles/RelatedArticles";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Image from "@/components/ui/image";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useIsMobile } from "@/hooks/use-mobile";
import { KayttokohteetCard } from "@/components/home";

const JoustoluottoPage = () => {
  const isMobile = useIsMobile();
  const seoKeywords = ["joustoluotto", "joustoluoton kilpailutus", "edullisin joustoluotto", "joustoluottovertailu", "jatkuva luotto", "joustava luotto", "luottoraja", "luottolimiitti"];

  const loanBenefits = [
    {
      icon: <BarChart3 className="h-6 w-6 text-blue-500" />,
      title: "Käytä tarpeen mukaan",
      description: "Nosta rahaa joustavasti silloin kun tarvitset ja juuri sen verran kuin haluat."
    },
    {
      icon: <Clock className="h-6 w-6 text-green-500" />,
      title: "Maksa vain käytöstä",
      description: "Maksat korkoa vain käyttämästäsi luotosta, ei koko luottorajasta."
    },
    {
      icon: <AlertCircle className="h-6 w-6 text-purple-500" />,
      title: "Jatkuva käyttöoikeus",
      description: "Maksettuasi luottoa takaisin voit nostaa sitä uudelleen ilman uutta hakemusta."
    },
    {
      icon: <ShieldCheck className="h-6 w-6 text-orange-500" />,
      title: "Ilman vakuuksia",
      description: "Saat joustoluoton käyttöösi ilman takaajia tai erillisiä vakuuksia."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SeoMetadata
        title="Joustoluotto 2024 | Kilpailuta helposti | Lainasampo"
        description="Vertaile joustoluottoja helposti Lainasampo.fi:n kautta. Kilpailuta joustoluottosi yhdellä hakemuksella ja säästä rahaa. Nopea päätös, edulliset korot."
        keywords={seoKeywords}
        canonicalUrl="https://lainasampo.fi/joustoluotto"
      />
      
      <div className="container mx-auto px-4 py-4">
        <Link 
          to="/lainatuotteet" 
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Takaisin lainatuotteisiin
        </Link>
      </div>
      
      {/* Hero section - Fixed height that spans full width */}
      <div className="relative h-[500px] md:h-[650px] w-full overflow-hidden">
        <Image 
          src="/lovable-uploads/d87a0ba4-9785-40af-bd0a-f23a93d27b34.png"
          alt="Tietokone ja kahvikuppi pöydällä" 
          className="absolute inset-0 w-full h-full object-cover"
          fallbackSrc="/placeholder.svg"
          priority={true}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent md:from-black/70 md:via-black/50 flex items-center">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-2xl text-white space-y-4 md:space-y-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight drop-shadow-lg">
                Kilpailuta <span className="text-blue-400">joustoluotto</span> helposti
              </h1>
              <p className="text-base sm:text-lg md:text-xl drop-shadow-md">
                Täytä yksi hakemus ja vertaile eri pankkien tarjouksia. Nosta rahaa joustavasti tarpeen mukaan.
              </p>
              <div className="pt-2 md:pt-4 flex flex-col sm:flex-row gap-3 md:gap-4">
                <LainaCta 
                  text={isMobile ? "Hae lainaa" : "Hae joustoluottoa nyt"} 
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold shadow-lg transform hover:scale-105 transition duration-300"
                  variant="primary"
                  size={isMobile ? "md" : "lg"}
                />
                {!isMobile && (
                  <Link to="/lainatuotteet" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 border border-white/30 backdrop-blur-sm text-white font-medium transition-all duration-300">
                    Vertaile lainoja <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 pt-10">
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-10 md:mb-12 relative z-10">
          {[
            { text: isMobile ? "Päätös 24h" : "Päätös 24h sisällä", icon: <Clock className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "Korot alk. 5,9%" : "Edulliset korot alkaen 5,9%", icon: <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "Ei vakuuksia" : "Ei vaadi vakuuksia", icon: <ShieldCheck className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> },
            { text: isMobile ? "Raja jopa 30 000€" : "Luottoraja jopa 30 000€", icon: <Wallet className="h-4 w-4 md:h-5 md:w-5 text-blue-500" /> }
          ].map((item, i) => (
            <div key={i} className="bg-white px-3 py-2 md:px-6 md:py-3 rounded-full shadow-md flex items-center gap-1.5 md:gap-2 text-sm md:text-base">
              {item.icon}
              <span className="font-medium">{item.text}</span>
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8 mb-8">
          <div className="lg:col-span-2 space-y-6 md:space-y-10">
            <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 md:mb-4">Mikä on joustoluotto?</h2>
                <p className="text-sm md:text-base text-gray-700 mb-3 md:mb-4">
                  Joustoluotto on joustava luottomuoto, joka tarjoaa sinulle mahdollisuuden nostaa rahaa tarpeen mukaan 
                  myönnetyn luottorajan puitteissa. Se toimii kuin luottokortti, mutta ilman fyysistä korttia. 
                  Voit nostaa rahaa tilillesi silloin kun tarvitset ja maksat korkoa vain käyttämästäsi luotosta.
                </p>
                <div className="space-y-2 md:space-y-3">
                  <h3 className="font-bold text-blue-700 text-sm md:text-base">Joustoluoton edut:</h3>
                  <ul className="space-y-1 md:space-y-2">
                    {[
                      "Nosta rahaa joustavasti tarpeen mukaan",
                      "Maksa korkoa vain käyttämästäsi luotosta",
                      "Ei vuosimaksuja tai piilokuluja",
                      "Luottoraja jopa 30 000€",
                      "Käsittelyaika vain 24h"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-sm md:text-base text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 md:mb-4">Kilpailuta joustoluottosi helposti</h2>
                <p className="text-sm md:text-base text-gray-700 mb-3 md:mb-4">
                  Lainasampo.fi välittää lainahakemuksesi yli 20 pankille ja rahoitusyhtiölle. Vertaile tarjouksia rauhassa ja valitse itsellesi sopivin vaihtoehto.
                </p>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 md:p-5 rounded-xl border border-blue-200">
                  <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-sm md:text-base">Joustoluoton tiedot</h3>
                  <div className="space-y-2 md:space-y-3">
                    {[
                      { label: "Luottoraja:", value: "1 000€ - 30 000€" },
                      { label: "Korko alkaen:", value: "5,9%" },
                      { label: "Nostopalkkio:", value: "Ei nostopalkkiota" },
                      { label: "Vakuus:", value: "Vakuudeton" }
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between text-sm md:text-base">
                        <span className="text-gray-700 font-medium">{item.label}</span>
                        <span className="font-bold text-blue-800">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="relative rounded-xl overflow-hidden mb-3 group transform hover:scale-[1.01] transition-all duration-300">
                  <div className="w-full h-[300px] md:h-[400px] bg-blue-100">
                    <img
                      src="/lovable-uploads/462fdda9-7ee7-4655-b892-96334f6ed6b9.png"
                      alt="Mies käyttää puhelinta joustoluoton hallintaan"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6 flex flex-col justify-end">
                    <h3 className="text-white text-lg md:text-xl font-bold mb-2">Joustava käyttö</h3>
                    <ul className="space-y-1 text-white text-sm">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Käytä luottoa tarpeen mukaan</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Nosta rahaa milloin haluat</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Maksa takaisin joustavasti</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <div className="relative rounded-xl overflow-hidden mb-3 group transform hover:scale-[1.01] transition-all duration-300">
                  <div className="w-full h-[300px] md:h-[400px] bg-blue-100">
                    <img
                      src="/lovable-uploads/9726d5f1-a60e-4bcb-90ca-265cdd6e9fa1.png"
                      alt="Nainen käyttää tietokonetta joustoluoton hallintaan"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6 flex flex-col justify-end">
                    <h3 className="text-white text-lg md:text-xl font-bold mb-2">Helppo hakea</h3>
                    <ul className="space-y-1 text-white text-sm">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Täytä hakemus verkossa</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Saat päätöksen nopeasti</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                        <span>Vertaile eri tarjouksia</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-6 relative">
                <span className="text-gray-800">
                  Joustoluoton hyödyt
                </span>
                <div className="absolute -bottom-2 md:-bottom-3 left-0 w-16 md:w-20 h-1 bg-blue-500"></div>
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-5">
                {loanBenefits.map((benefit, index) => (
                  <div key={index} className="relative overflow-hidden group">
                    <div className={`absolute inset-0 bg-${index === 0 ? 'blue' : index === 1 ? 'green' : index === 2 ? 'purple' : 'orange'}-50 rounded-xl transform translate-y-2 translate-x-2 group-hover:translate-y-1 group-hover:translate-x-1 transition-transform duration-300`}></div>
                    <div className={`p-3 md:p-5 bg-white border border-${index === 0 ? 'blue' : index === 1 ? 'green' : index === 2 ? 'purple' : 'orange'}-100 rounded-xl relative group-hover:shadow-md transition-all`}>
                      <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-${index === 0 ? 'blue' : index === 1 ? 'green' : index === 2 ? 'purple' : 'orange'}-500 to-${index === 0 ? 'blue' : index === 1 ? 'green' : index === 2 ? 'purple' : 'orange'}-700 flex items-center justify-center mb-2 md:mb-3`}>
                        {benefit.icon}
                      </div>
                      <h4 className={`font-bold text-${index === 0 ? 'blue' : index === 1 ? 'green' : index === 2 ? 'purple' : 'orange'}-800 mb-1 md:mb-2 text-sm md:text-base`}>{benefit.title}</h4>
                      <p className="text-xs md:text-sm text-gray-700">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-6 relative">
                <span className="text-gray-800">
                  Mihin joustoluottoa voi käyttää?
                </span>
                <div className="absolute -bottom-2 md:-bottom-3 left-0 w-16 md:w-20 h-1 bg-blue-500"></div>
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {[
                  {
                    icon: <CreditCard className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Ostokset ja hankinnat",
                    description: "Käytä joustoluottoa kodin hankintoihin, elektroniikkaan tai vaikkapa vaatteisiin."
                  },
                  {
                    icon: <Wallet className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Yllättävät menot",
                    description: "Varaudu yllättäviin menoihin kuten kodinkoneiden rikkoutumiseen tai auton korjauksiin."
                  },
                  {
                    icon: <Clock className="h-8 w-8 md:h-10 md:w-10 text-white" />,
                    title: "Matkustaminen",
                    description: "Rahoita lomamatkat, viikonloppureissut tai muut matkakulut joustavasti."
                  }
                ].map((feature, index) => (
                  <div key={index} className="relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl transform group-hover:scale-105 transition-transform duration-300"></div>
                    <Card className="h-full bg-transparent border-0 relative z-10 bg-white/95 backdrop-blur-sm group-hover:bg-white/100 transition-all">
                      <CardContent className="pt-4 md:pt-6">
                        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-r from-blue-600 to-blue-800 flex items-center justify-center mb-2 md:mb-3">
                          {feature.icon}
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-blue-800 mb-1 md:mb-2">{feature.title}</h3>
                        <p className="text-xs md:text-sm text-gray-700">{feature.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </section>
            
            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-6 relative">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-blue-600">
                  Usein kysytyt kysymykset
                </span>
                <div className="absolute -bottom-2 md:-bottom-3 left-0 w-16 md:w-20 h-1 bg-blue-500"></div>
              </h2>
              
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-1" className="bg-white rounded-lg border border-gray-200">
                  <AccordionTrigger className="px-4 py-3 text-left hover:no-underline w-full">
                    Miten joustoluotto toimii?
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3 text-gray-600">
                    Joustoluotto toimii kuin jatkuva luotto: saat käyttöösi luottorajan, josta voit nostaa rahaa 
                    tarpeen mukaan. Maksat takaisin kuukausierissä ja voit nostaa lisää rahaa sitä mukaa kun maksat 
                    luottoa takaisin. Maksat korkoa vain käyttämästäsi luotosta.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="bg-white rounded-lg border border-gray-200">
                  <AccordionTrigger className="px-4 py-3 text-left hover:no-underline w-full">
                    Mitä joustoluoton hakemiseen tarvitaan?
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3 text-gray-600">
                    Hakemiseen tarvitset:
                    <ul className="list-disc pl-5 mt-2">
                      <li>Vähintään 20 vuoden ikä</li>
                      <li>Säännölliset tulot</li>
                      <li>Kunnossa olevat luottotiedot</li>
                      <li>Suomalainen pankkitili</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="bg-white rounded-lg border border-gray-200">
                  <AccordionTrigger className="px-4 py-3 text-left hover:no-underline w-full">
                    Miten takaisinmaksu toimii?
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3 text-gray-600">
                    Maksat kuukausittain vähintään minimilyhennyksen, joka on tyypillisesti 3-4% käytetystä luotosta. 
                    Voit aina maksaa enemmän ja näin pienentää korkokuluja. Takaisinmaksettu luotto on heti uudelleen käytettävissäsi.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="bg-white rounded-lg border border-gray-200">
                  <AccordionTrigger className="px-4 py-3 text-left hover:no-underline w-full">
                    Mihin joustoluottoa voi käyttää?
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3 text-gray-600">
                    Joustoluottoa voit käyttää mihin tahansa tarkoitukseen, kuten:
                    <ul className="list-disc pl-5 mt-2">
                      <li>Yllättäviin menoihin</li>
                      <li>Kodin hankintoihin</li>
                      <li>Remontointiin</li>
                      <li>Laskujen maksamiseen</li>
                      <li>Matkustamiseen</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5" className="bg-white rounded-lg border border-gray-200">
                  <AccordionTrigger className="px-4 py-3 text-left hover:no-underline w-full">
                    Voiko joustoluottoa korkoa kilpailuttaa?
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3 text-gray-600">
                    Kyllä, joustoluoton voi kilpailuttaa. Vertailemalla eri pankkien ja rahoituslaitosten tarjoamia 
                    joustoluottoja voit säästää merkittävästi korkokuluissa. Lainasampo auttaa sinua kilpailuttamaan 
                    joustoluottosi helposti yhdellä hakemuksella.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>
          </div>
          
          <div className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <div className="relative overflow-hidden rounded-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800"></div>
                <div className="absolute top-0 left-0 w-full h-full opacity-10">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path d="M0,0 L100,0 L100,100 L0,100 Z" fill="url(#pattern)"></path>
                    <defs>
                      <pattern id="pattern" patternUnits="userSpaceOnUse" width="10" height="10">
                        <circle cx="5" cy="5" r="1" fill="white"></circle>
                      </pattern>
                    </defs>
                  </svg>
                </div>
                <div className="relative p-6 text-white">
                  <h2 className="text-2xl font-bold mb-4">Kilpailuta joustoluottosi nyt!</h2>
                  <p className="mb-6">
                    Täyttämällä yhden hakemuksen saat useita tarjouksia eri pankeilta. Vertaa rauhassa ja valitse edullisin!
                  </p>
                  <LainaCta 
                    variant="primary" 
                    size="lg" 
                    text="Hae joustoluottoa" 
                    className="w-full bg-white text-blue-700 hover:bg-blue-50 font-bold shadow-md"
                  />
                </div>
              </div>
              
              <Card className="border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
                  <CardTitle className="text-lg text-white">Joustoluotto lyhyesti</CardTitle>
                </div>
                <CardContent className="pt-5">
                  <div className="space-y-3">
                    {[
                      { label: "Luottoraja:", value: "1 000€ - 30 000€" },
                      { label: "Korko alkaen:", value: "5,9%" },
                      { label: "Minimilyhennys:", value: "3-4% / kk" },
                      { label: "Vakuus:", value: "Ei tarvita" },
                      { label: "Käsittelyaika:", value: "1-2 arkipäivää" }
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-bold text-blue-800">{item.value}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-bold">Säästövinkki:</span> Kilpailuttamalla voit säästää jopa 25% joustoluottosi koroissa!
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="font-bold text-lg mb-4 text-gray-800">Katso myös nämä rahoitusvaihtoehdot</h3>
                <div className="space-y-3">
                  {[
                    { name: "Kulutusluotto", url: "/lainat/kulutusluotto" },
                    { name: "Autolaina", url: "/lainat/autolaina" },
                    { name: "Remonttilaina", url: "/lainat/remonttilaina" },
                    { name: "Yhdistelylaina", url: "/lainat/yhdistelylaina" }
                  ].map((option, i) => (
                    <Link
                      key={i}
                      to={option.url}
                      className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-100 text-gray-800 hover:text-blue-700 font-medium transition-colors group"
                    >
                      <span>{option.name}</span>
                      <ArrowRight className="ml-auto h-4 w-4 text-gray-400 group-hover:text-blue-500 transform group-hover:translate-x-1 transition-all" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <section className="mb-8 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-8">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
              Joustoluoton yleisimmät käyttökohteet
            </span>
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            <KayttokohteetCard
              title="Joustava käyttö"
              description="Nosta rahaa tilillesi milloin haluat"
              bgColor="bg-blue-100"
              imagePath="/lovable-uploads/5f4ed836-e405-410a-8ac5-97aff8d53f8c.png"
            />
            <KayttokohteetCard
              title="Helppo maksaminen"
              description="Voit maksaa ostoksia suoraan luotolta"
              bgColor="bg-green-100"
              imagePath="/lovable-uploads/69cdf380-f1b0-4e3d-bc1b-2f3f623d9e46.png"
            />
            <KayttokohteetCard
              title="Online hallinta"
              description="Hallitse luottoa verkkopalvelussa"
              bgColor="bg-amber-100"
              imagePath="/lovable-uploads/a9b403d9-d5c1-463f-9503-94399f8f2682.png"
            />
          </div>
        </section>
        
        <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl md:rounded-2xl overflow-hidden mb-8 md:mb-16 relative">
          <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path fill="white" d="M48.8,-50.9C62.8,-41.7,73.2,-24.3,76.4,-5.3C79.6,13.7,75.5,34.3,63.1,47.6C50.6,60.9,29.8,66.9,9.7,70.7C-10.4,74.5,-30,76.1,-45.2,67.4C-60.4,58.8,-71.3,39.9,-75.9,19.8C-80.5,-0.3,-78.9,-21.5,-68.9,-35.6C-58.9,-49.7,-40.5,-56.7,-23.7,-64.6C-6.9,-72.5,8.4,-81.3,22.7,-77.1C37.1,-72.9,50.4,-55.7,48.8,-50.9Z" transform="translate(100 100)" />
            </svg>
          </div>
          <div className="container mx-auto px-4 md:px-6 py-8 md:py-12 relative z-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Kilpailuta joustoluottosi nyt!</h2>
                <p className="text-blue-100 max-w-2xl">
                  Täyttämällä yhden hakemuksen saat useita tarjouksia eri pankeilta. Vertaa rauhassa ja valitse edullisin!
                </p>
              </div>
              <LainaCta
                text="Hae joustoluottoa"
                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-3 font-medium min-w-[200px]"
                variant="primary"
                size="lg"
              />
            </div>
          </div>
        </section>

        <RelatedArticles
          productType="joustoluotto"
          title="Lue lisää joustoluotosta"
        />
      </div>
    </div>
  );
};

export default JoustoluottoPage;
